// src/pages/ArchetypePreview.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Heart, Shield, Music } from "lucide-react";
import { RecipientFlow } from "../components/recipient/RecipientFlow";
import { CardData } from "../types/card.types";
import { ARCHETYPES, ArchetypeId } from "../constants/archetypes";
import logo from "../assets/img/logo.png";

// Helper to create a preview card from archetype
function getArchetypePreviewCard(archetype: ArchetypeId): CardData {
  const config = ARCHETYPES[archetype];
  const now = new Date().toISOString();

  // Create appropriate recipient name and type based on archetype
  let recipientName = "Beautiful Soul";
  let recipientType: CardData["recipientType"] = "partner";

  if (archetype === "moments") {
    recipientName = "Celebrator";
    recipientType = "self_celebration";
  }
  if (archetype === "heartbeat") {
    recipientName = "Beloved";
    recipientType = "partner";
  }
  if (archetype === "safehaven") {
    recipientName = "Treasured One";
    recipientType = "self_healing";
  }

  return {
    id: `preview-${archetype}`,
    shareableLink: `/preview/archetype/${archetype}`,
    senderId: "preview",
    senderEmail: "preview@lrnsy.com",
    senderName: "Someone Who Cares",
    recipientName: recipientName,
    recipientType: recipientType,
    recipientImage: null,
    theme: archetype === "moments" ? "luxury" : archetype === "heartbeat" ? "valentine" : "calm",
    title: config.defaultCardData.title || "A Special Message",
    message: config.defaultCardData.message || "",
    personalNote: config.defaultCardData.personalNote,
    primaryColor: config.defaultCardData.primaryColor || "#ffffff",
    secondaryColor: config.defaultCardData.secondaryColor || "#cccccc",
    accentColor: config.defaultCardData.accentColor || "#aaaaaa",
    fontFamily: config.defaultCardData.fontFamily || "Inter",
    includeBouquet: true,
    bouquetAmount: 0,
    petals: 0,
    includeHeartParticles: config.defaultCardData.includeHeartParticles || false,
    includeSparkles: config.defaultCardData.includeSparkles || false,
    status: "paid",
    paymentRef: null,
    createdAt: now,
    updatedAt: now,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    viewedAt: null,
    acceptedAt: null,
    archetype,
    privacyMode: "standard_private",
    accessStatus: "active",
    encryptedMessage: null,
    encryptedPersonalNote: null,
    encryptedMemory: null,
    encryptedInsideJoke: null,
    encryptionVersion: null,
    encryptionSalt: null,
    encryptionIv: null,
    encryptedKey: null,
    keyWrapVersion: null,
    requiresPasscode: false,
    deleteAfterOpen: false,
    openedCount: 0,
    lastOpenedAt: null,
  };
}

export default function ArchetypePreview() {
  const { archetypeId } = useParams<{ archetypeId: string }>();
  const navigate = useNavigate();
  const [previewCard, setPreviewCard] = useState<CardData | null>(null);
  const [showStartPrompt, setShowStartPrompt] = useState(true);

  useEffect(() => {
    if (archetypeId && ARCHETYPES[archetypeId as ArchetypeId]) {
      setPreviewCard(getArchetypePreviewCard(archetypeId as ArchetypeId));
    } else {
      navigate("/archetypes");
    }
  }, [archetypeId, navigate]);

  const handleBack = () => {
    navigate("/archetypes");
  };

  const handleCreateYourOwn = () => {
    if (archetypeId) {
      navigate(`/create?archetype=${archetypeId}`);
    }
  };

  if (!previewCard) return null;

  const archConfig = ARCHETYPES[archetypeId as ArchetypeId];

  const handleStartExperience = () => {
    setShowStartPrompt(false);
    // The user interaction will trigger audio
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      <RecipientFlow card={previewCard} isPreview={true} shouldAutoAdvance={false} />

      {/* Preview Controls Header */}
      {/* Preview Controls Header & Footer */}
      <div className="fixed inset-0 pointer-events-none z-[9999]">
        {/* TOP BAR: Back & Status */}
        <div className="absolute top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center gap-2">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleBack}
            className="pointer-events-auto group flex items-center gap-2 md:gap-3 bg-black/40 hover:bg-white/10 backdrop-blur-2xl text-white border border-white/10 px-4 md:px-5 py-2.5 md:py-3 rounded-full transition-all active:scale-95"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] whitespace-nowrap">
              <span className="hidden xs:inline">Back to </span>Archetypes
            </span>
          </motion.button>

          {/* Previewing Status - Becomes more compact on mobile */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="pointer-events-auto flex items-center gap-2 md:gap-4 bg-black/40 backdrop-blur-2xl border border-white/10 px-4 md:px-5 py-2.5 md:py-3 rounded-full"
          >
            <div className="flex items-center gap-2">
              {archetypeId === "moments" && <Sparkles size={12} className="text-yellow-500" />}
              {archetypeId === "heartbeat" && <Heart size={12} className="text-pink-500" />}
              {archetypeId === "safehaven" && <Shield size={12} className="text-blue-500" />}
              <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-white/70 whitespace-nowrap">
                <span className="hidden sm:inline">Previewing:</span> {archConfig?.name}
              </span>
            </div>
          </motion.div>

          {/* DESKTOP ONLY: Create Button (Hidden on Mobile top bar) */}
          <motion.button
            onClick={handleCreateYourOwn}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden lg:flex pointer-events-auto font-black uppercase text-black items-center gap-4 bg-white border border-black/10 px-6 py-3 rounded-full hover:bg-gray-100 transition-colors"
          >
            <span className="text-xs">Create This Experience</span>
            <ArrowRight size={18} />
          </motion.button>
        </div>

        {/* MOBILE ONLY: Floating Bottom Action (Visible only on Mobile/Tablet) */}
        <div className="lg:hidden absolute bottom-8 left-0 right-0 px-6 flex justify-center">
          <motion.button
            onClick={handleCreateYourOwn}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={{ scale: 0.95 }}
            className="pointer-events-auto w-full max-w-sm font-black uppercase text-black flex items-center justify-between bg-white shadow-[0_20px_50px_rgba(0,0,0,0.5)] px-6 py-5 rounded-2xl"
          >
            <div className="flex flex-col items-start">
              <span className="text-[8px] opacity-50 tracking-widest">Selected Style</span>
              <span className="text-xs">{archConfig?.name}</span>
            </div>
            <div className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg">
              <span className="text-[10px]">Create</span>
              <ArrowRight size={14} />
            </div>
          </motion.button>
        </div>
      </div>
      {showStartPrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.1, filter: "blur(20px)" }}
          transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden"
          style={{
            background: `radial-gradient(circle at center, #000 0%, ${
              archConfig?.defaultCardData.secondaryColor || "#000000"
            } 100%)`,
          }}
        >
          {/* 1. CINEMATIC AMBIENCE: Deep, slow-breathing light */}
          <motion.div
            animate={{
              opacity: [0.2, 0.4, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at center, ${
                archConfig?.defaultCardData.primaryColor || "#E11D48"
              }15 0%, transparent 70%)`,
            }}
          />

          {/* 2. ATMOSPHERIC PARTICLES: Dust motes in a spotlight */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-white"
                style={{
                  width: Math.random() * 2 + 1 + "px",
                  height: Math.random() * 2 + 1 + "px",
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  opacity: 0,
                }}
                animate={{
                  y: [0, -100],
                  opacity: [0, 0.3, 0],
                }}
                transition={{
                  duration: 10 + Math.random() * 10,
                  repeat: Infinity,
                  delay: Math.random() * 5,
                  ease: "linear",
                }}
              />
            ))}
          </div>

          {/* 3. THE CENTERPIECE: Minimalist Typography & Icon */}
          <div className="relative z-10 flex flex-col items-center max-w-sm px-8">
            {/* Icon: Floating without a heavy container */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="mb-12 relative"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="mb-12 relative flex items-center justify-center"
              >
                {/* The Hero Logo */}
                <motion.div
                  animate={{
                    y: [0, -8, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative z-10"
                >
                  {/* Replace /logo.png with your actual logo path */}
                  <img
                    src={logo}
                    alt="LRNSY Logo"
                    className="w-20 h-20 md:w-24 md:h-24 object-contain brightness-110"
                  />
                </motion.div>

                {/* Ambient Halo: Creates depth behind the logo */}
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 blur-3xl rounded-full scale-150"
                  style={{
                    background: `radial-gradient(circle, ${
                      archConfig?.defaultCardData.primaryColor || "#fff"
                    }44 0%, transparent 70%)`,
                  }}
                />
              </motion.div>

              {/* Soft glow behind icon */}
              <div className="absolute inset-0 blur-2xl opacity-50 bg-white/20 scale-150" />
            </motion.div>

            {/* Title: Using the Airbnb/Apple hierarchy */}
            <div className="text-center space-y-4 mb-16">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.4 }}
                transition={{ delay: 0.5, duration: 1 }}
                className="text-[10px] uppercase tracking-[0.5em] text-white"
              >
                You are invited to
              </motion.p>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 1.2 }}
                className="text-4xl md:text-5xl font-serif italic text-white font-light"
              >
                {archConfig?.name}
              </motion.h2>

              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "40px" }}
                transition={{ delay: 1.2, duration: 1.5 }}
                className="h-px bg-white/20 mx-auto mt-6"
              />
            </div>

            {/* 4. THE INTERACTION: High-intent button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 1 }}
              onClick={handleStartExperience}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group relative flex flex-col items-center gap-4"
            >
              <div className="px-12 py-5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-white transition-all group-hover:bg-white group-hover:text-black group-hover:border-transparent overflow-hidden">
                <div className="relative z-10 flex items-center gap-3">
                  <span className="text-xs uppercase tracking-[0.3em] font-medium">
                    Begin Experience
                  </span>
                  <ArrowRight
                    size={14}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </div>

                {/* Subtle light sweep */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-shimmer"
                  initial={false}
                />
              </div>

              <div className="flex items-center gap-2 opacity-30 group-hover:opacity-60 transition-opacity">
                <Music size={10} />
                <span className="text-[9px] uppercase tracking-widest font-light">
                  Sound Recommended
                </span>
              </div>
            </motion.button>
          </div>

          {/* 5. FOOTER: Signature */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ delay: 2.5 }}
            className="absolute bottom-12"
          >
            <span className="text-[10px] tracking-[0.8em] text-white uppercase font-light">
              LRNSY
            </span>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
